import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Storage Structure (Client-focused with RBT index):
 *
 * clients/
 *   └── {clientId}/
 *       └── sessions/
 *           └── {YYYY-MM-DD}_{sessionNoteId}/
 *               ├── verification-photo.jpg
 *               ├── recording.mp4 (future)
 *               └── approved-note.pdf
 *
 * This structure allows:
 * - Easy audit retrieval of all client files
 * - Date-based organization within each client
 * - All session artifacts grouped together
 */

export interface SessionStoragePaths {
  basePath: string;
  verificationPhoto: string;
  recording: string;
  approvedPdf: string;
}

export class StorageService {
  private static instance: StorageService;

  private constructor() {}

  static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }

  /**
   * Generate all storage paths for a session
   * @param clientId Client ID
   * @param sessionNoteId Session Note ID
   * @param sessionDate Date of the session
   * @returns Object with all relevant storage paths
   */
  generateSessionPaths(
    clientId: string,
    sessionNoteId: string,
    sessionDate: Date
  ): SessionStoragePaths {
    const dateStr = this.formatDateForPath(sessionDate);
    const basePath = `clients/${clientId}/sessions/${dateStr}_${sessionNoteId}`;

    return {
      basePath,
      verificationPhoto: `${basePath}/verification-photo.jpg`,
      recording: `${basePath}/recording.mp4`,
      approvedPdf: `${basePath}/approved-note.pdf`,
    };
  }

  /**
   * Format date as YYYY-MM-DD for storage paths
   */
  private formatDateForPath(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Upload verification photo to client-focused storage structure
   * @param photoBlob Photo blob to upload
   * @param clientId Client ID
   * @param sessionNoteId Session Note ID
   * @param sessionDate Date of the session
   * @param onProgress Progress callback
   * @returns Download URL
   */
  async uploadVerificationPhoto(
    photoBlob: Blob,
    clientId: string,
    sessionNoteId: string,
    sessionDate: Date,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const paths = this.generateSessionPaths(clientId, sessionNoteId, sessionDate);
    return this.uploadFile(photoBlob as File, paths.verificationPhoto, onProgress);
  }

  /**
   * Upload approved PDF to client-focused storage structure
   * @param pdfBlob PDF blob to upload
   * @param clientId Client ID
   * @param sessionNoteId Session Note ID
   * @param sessionDate Date of the session
   * @param onProgress Progress callback
   * @returns Download URL
   */
  async uploadApprovedPdf(
    pdfBlob: Blob,
    clientId: string,
    sessionNoteId: string,
    sessionDate: Date,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const paths = this.generateSessionPaths(clientId, sessionNoteId, sessionDate);
    return this.uploadFile(pdfBlob as File, paths.approvedPdf, onProgress);
  }

  /**
   * Upload session recording to client-focused storage structure
   * @param recordingBlob Recording blob to upload
   * @param clientId Client ID
   * @param sessionNoteId Session Note ID
   * @param sessionDate Date of the session
   * @param onProgress Progress callback
   * @returns Download URL
   */
  async uploadSessionRecording(
    recordingBlob: Blob,
    clientId: string,
    sessionNoteId: string,
    sessionDate: Date,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const paths = this.generateSessionPaths(clientId, sessionNoteId, sessionDate);
    return this.uploadFile(recordingBlob as File, paths.recording, onProgress);
  }

  /**
   * Upload a file to Firebase Storage with progress tracking
   * @param file The file to upload
   * @param path The storage path (e.g., 'footage/companyId/fileName.mp4')
   * @param onProgress Callback for upload progress (0-100)
   * @returns Promise with the download URL
   */
  async uploadFile(
    file: File,
    path: string,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(Math.round(progress));
          }
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  /**
   * Upload multiple files with individual progress tracking
   * @param files Array of files to upload
   * @param pathPrefix Base path for uploads (e.g., 'footage/companyId/')
   * @param onFileProgress Callback for individual file progress
   * @returns Promise with array of download URLs
   */
  async uploadMultipleFiles(
    files: File[],
    pathPrefix: string,
    onFileProgress?: (fileName: string, progress: number) => void
  ): Promise<string[]> {
    const uploadPromises = files.map((file) => {
      const fileName = `${Date.now()}_${file.name}`;
      const path = `${pathPrefix}/${fileName}`;

      return this.uploadFile(file, path, (progress) => {
        if (onFileProgress) {
          onFileProgress(file.name, progress);
        }
      });
    });

    return Promise.all(uploadPromises);
  }

  /**
   * Delete a file from Firebase Storage
   * @param url The download URL or storage path
   */
  async deleteFile(url: string): Promise<void> {
    try {
      const storageRef = ref(storage, url);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  }

  /**
   * Generate a storage path for footage
   * @param companyId Company ID
   * @param userId User ID
   * @param fileName Original file name
   * @param date Date of the footage
   * @returns Storage path string
   */
  generateFootagePath(
    companyId: string,
    userId: string,
    fileName: string,
    date: Date
  ): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const timestamp = Date.now();
    const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');

    return `footage/${companyId}/${year}/${month}/${day}/${userId}_${timestamp}_${cleanFileName}`;
  }
}

export const storageService = StorageService.getInstance();
